import { IntegrationServices, useMutation } from '@sequencehq/api'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import { useNotifications } from '@sequencehq/core-components'

type ImportSubmitArgs = {
  selectedIntegration: IntegrationServices | null
  selectedIdsInView: string[]
  integrationName: string | null
}

export type ImportSubmitResult = {
  started: boolean
  startedAt?: string
}

export type ImportSubmit = {
  onSubmit: () => Promise<ImportSubmitResult>
  isSubmitting: boolean
}

export const useLegacyImportSubmit = (args: ImportSubmitArgs): ImportSubmit => {
  const { displayNotification } = useNotifications()

  const linkCustomersMutation = useMutation(
    dashboard20240730Client.postCreateCustomerFromExternalService,
    {
      onError: () =>
        displayNotification(
          `Failed to start import for customers from ${args.integrationName}`,
          { type: 'error' }
        )
    }
  )

  const onSubmit = async (): Promise<ImportSubmitResult> => {
    if (!args.selectedIntegration || args.selectedIdsInView.length === 0) {
      return { started: false }
    }
    const res = await linkCustomersMutation.mutateAsync({
      service: args.selectedIntegration,
      customerIds: args.selectedIdsInView
    })
    return { started: Boolean(res?.count) }
  }

  return { onSubmit, isSubmitting: linkCustomersMutation.isPending }
}

export const useSyncImportSubmit = (args: ImportSubmitArgs): ImportSubmit => {
  const { displayNotification } = useNotifications()

  const syncHubspotCustomersMutation = useMutation(
    dashboard20240730Client.postSyncHubspotCustomers,
    {
      onError: () =>
        displayNotification(
          `Failed to start import for customers from ${args.integrationName}`,
          { type: 'error' }
        )
    }
  )

  const onSubmit = async (): Promise<ImportSubmitResult> => {
    if (args.selectedIdsInView.length === 0) {
      return { started: false }
    }
    const customers = args.selectedIdsInView.map(id => ({
      id,
      primaryContactId: null
    }))
    const res = await syncHubspotCustomersMutation.mutateAsync({ customers })
    if (!res?.taskId) {
      return { started: false }
    }
    return { started: true, startedAt: res.startedAt }
  }

  return { onSubmit, isSubmitting: syncHubspotCustomersMutation.isPending }
}
